import api from 'services/api';
import { array, enforce, num, object, runSchema, str } from 'validation';

// Source of truth for the wire shape: nap-backend `routes/queues.js` GET /queues, which responds
// `{ queues, pagination, config, filtersConfig }`. Only `queues` is consumed here, and each queue row
// carries far more than an LOV needs (limit, status, workers, folder, …). The projection narrows to
// the identifiers an LOV row renders and keys on.

export interface QueueSummary {
  readonly queue_id: number;
  readonly queue_name: string;
  readonly zone_name: string;
}

const summarySchema = object<QueueSummary>({
  queue_id: num(),
  queue_name: str(),
  zone_name: str(),
});

interface QueuesResponse {
  readonly queues: readonly QueueSummary[];
}

const responseSchema = object<QueuesResponse>({
  queues: array(summarySchema),
});

const LOV_LIMIT = 2000;

/**
 * Fetch all queues the current user can see, for a picker. No pagination — the LOV needs the complete
 * set so Typeahead's client-side filter has every option to match against. If the universe of queues
 * ever grows past a UX-bearable dropdown size, this is the call site to migrate to server-side search.
 */
export const getQueuesLOV = async (): Promise<readonly QueueSummary[]> => {
  const res = await api.get('/queues', {
    params: { limit: LOV_LIMIT },
  });
  const parsed = runSchema(responseSchema, res.data, 'getQueuesLOV');
  if (!parsed.ok) {
    throw new Error('getQueuesLOV: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['queues']).queues;
};
